/**
 * Edge Functions Configuration
 * ============================
 * Manages switching between local and production Edge Functions.
 *
 * Configuration is controlled via environment variable:
 * - Set VITE_USE_LOCAL_EDGE_FUNCTIONS=true in your .env for local development
 * - Set VITE_USE_LOCAL_EDGE_FUNCTIONS=false or leave unset for production
 *
 * This ensures each developer can control their environment without affecting others.
 *
 * Port Configuration (Exercise Toolbox uses 54600 series):
 * - Edge Functions: 54600 (http://127.0.0.1:54600/functions/v1)
 */

export interface EdgeFunctionsConfig {
  url: string
  isLocal: boolean
}

/**
 * Check if a URL looks like a production Supabase URL
 */
function isProductionUrl(url: string): boolean {
  return url.includes('supabase.co') || url.includes('supabase.in')
}

/**
 * Clean up a URL by ensuring it doesn't have double /functions/v1
 */
function cleanEdgeFunctionsUrl(baseUrl: string): string {
  // Remove trailing slash
  let url = baseUrl.replace(/\/+$/, '')
  // If the URL already ends with /functions/v1, use as-is
  if (url.endsWith('/functions/v1')) {
    return url
  }
  // Otherwise, append /functions/v1
  return `${url}/functions/v1`
}

export const EDGE_FUNCTIONS_CONFIG = {
  /**
   * Toggle between local and production Edge Functions
   * Controlled by VITE_USE_LOCAL_EDGE_FUNCTIONS environment variable
   * Default: false (use production Edge Functions for safety)
   */
  USE_LOCAL_EDGE_FUNCTIONS:
    import.meta.env.VITE_USE_LOCAL_EDGE_FUNCTIONS === 'true',

  /**
   * Local Edge Functions URL (from `npx supabase functions serve` output)
   * Exercise Toolbox uses port 54600
   */
  LOCAL_EDGE_FUNCTIONS_URL:
    import.meta.env.VITE_LOCAL_EDGE_FUNCTIONS_URL ||
    'http://127.0.0.1:54600/functions/v1',

  /**
   * Production Edge Functions URL (derived from Supabase URL)
   */
  PRODUCTION_EDGE_FUNCTIONS_URL: import.meta.env.VITE_SUPABASE_URL
    ? cleanEdgeFunctionsUrl(import.meta.env.VITE_SUPABASE_URL)
    : '',

  /**
   * Get the current Edge Functions configuration based on environment settings
   * Returns the appropriate URL for local or production
   *
   * Safety feature: If local mode is enabled but URL looks like production,
   * it will warn and refuse to create the client
   */
  getEdgeFunctionsConfig(): EdgeFunctionsConfig {
    if (this.USE_LOCAL_EDGE_FUNCTIONS) {
      // Safety check: Make sure we're not accidentally using production URL in local mode
      if (isProductionUrl(this.LOCAL_EDGE_FUNCTIONS_URL)) {
        console.error(
          '🚨 SAFETY ERROR: VITE_USE_LOCAL_EDGE_FUNCTIONS=true but VITE_LOCAL_EDGE_FUNCTIONS_URL contains a production domain!'
        )
        console.error(
          '   Local URL should be http://127.0.0.1:54600/functions/v1 or similar, not a supabase.co domain.'
        )
        throw new Error(
          'Invalid configuration: Local mode enabled but production URL detected. Check your .env file.'
        )
      }

      console.log('🔧 Using LOCAL Edge Functions')
      console.log('=== Edge Functions Configuration ===')
      console.log(`URL: ${this.LOCAL_EDGE_FUNCTIONS_URL}`)
      console.log('Mode: LOCAL')
      console.log('====================================')

      return {
        url: this.LOCAL_EDGE_FUNCTIONS_URL,
        isLocal: true,
      }
    }

    console.log('🚀 Using PRODUCTION Edge Functions')
    console.log('=== Edge Functions Configuration ===')
    console.log(`URL: ${this.PRODUCTION_EDGE_FUNCTIONS_URL}`)
    console.log('Mode: PRODUCTION')
    console.log('====================================')

    return {
      url: this.PRODUCTION_EDGE_FUNCTIONS_URL,
      isLocal: false,
    }
  },

  /**
   * Get the appropriate Edge Functions base URL (legacy method for compatibility)
   */
  getEdgeFunctionsUrl(): string {
    return this.getEdgeFunctionsConfig().url
  },

  /**
   * Build a full URL for a specific Edge Function
   */
  getFunctionUrl(functionName: string): string {
    const baseUrl = this.getEdgeFunctionsUrl()
    return `${baseUrl}/${functionName}`
  },

  /**
   * Check if we're using local Edge Functions
   */
  isUsingLocalEdgeFunctions(): boolean {
    return this.USE_LOCAL_EDGE_FUNCTIONS
  },

  /**
   * Validate that required configuration is present
   * Returns true if valid, throws error if invalid
   */
  validate(): boolean {
    const config = this.USE_LOCAL_EDGE_FUNCTIONS
      ? { url: this.LOCAL_EDGE_FUNCTIONS_URL, mode: 'local' }
      : { url: this.PRODUCTION_EDGE_FUNCTIONS_URL, mode: 'production' }

    if (!config.url) {
      throw new Error(
        `Missing Edge Functions URL for ${config.mode} mode. Check your .env file.`
      )
    }

    return true
  },

  /**
   * Log current configuration status (detailed)
   */
  logStatus() {
    console.log('📊 Edge Functions Configuration Status:')
    console.log(
      `   Mode: ${this.USE_LOCAL_EDGE_FUNCTIONS ? 'LOCAL' : 'PRODUCTION'}`
    )
    console.log(
      `   URL: ${this.USE_LOCAL_EDGE_FUNCTIONS ? this.LOCAL_EDGE_FUNCTIONS_URL : this.PRODUCTION_EDGE_FUNCTIONS_URL}`
    )
  },
}

/**
 * Helper to toggle Edge Functions mode at runtime (primarily for testing)
 * Note: This does NOT recreate connections - for that you'd need to reload the page
 */
export function setEdgeFunctionsMode(useLocal: boolean) {
  EDGE_FUNCTIONS_CONFIG.USE_LOCAL_EDGE_FUNCTIONS = useLocal
  console.log(
    `Edge Functions mode changed to: ${useLocal ? 'LOCAL' : 'PRODUCTION'}`
  )
}

// For backwards compatibility with existing code
export const edgeFunctionsConfig = EDGE_FUNCTIONS_CONFIG

/**
 * Helper to call an Edge Function with standard headers
 */
export async function callEdgeFunction<T>(
  functionName: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
    body?: Record<string, unknown>
    token?: string
  } = {}
): Promise<T> {
  const { method = 'POST', body, token } = options
  const url = EDGE_FUNCTIONS_CONFIG.getFunctionUrl(functionName)

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ error: 'Unknown error' }))
    // Backend returns { error: "message" }, frontend was looking for { message: "..." }
    const errorMessage = errorData.error || errorData.message || `Edge Function error: ${response.status}`
    throw new Error(errorMessage)
  }

  return response.json()
}
